/**
 * VoicePeerConnection — one peer-to-peer voice link via RTCPeerConnection.
 *
 * Implements the "perfect negotiation" pattern (MDN reference) — the only
 * signaling-race-free way to run symmetric WebRTC in a game. Key state:
 *
 *   - `makingOffer`: local side is currently creating an offer
 *   - `ignoreOffer`: remote sent an offer while we were making one AND
 *                    we're the impolite side — drop the remote offer
 *   - `polite`: boolean assigned at peer-pair setup; smaller peer ID = polite
 *
 * Audio track and DataChannel lifecycle:
 *   - addTrack(local mic) on construction
 *   - 'voice-control' reliable DataChannel for PTT / mute / presence
 *   - 'voice-rt' unreliable DataChannel (maxRetransmits: 0) for VAD / position
 *
 * The class exposes `onRemoteStream` for callers to wire the incoming
 * `MediaStream` into their Web Audio spatial-audio graph.
 */

import type { VoiceChatConfig } from './VoiceChatConfig.js';
import { VOICE_PRESETS } from './VoiceChatConfig.js';
import type { IVoiceSignaling, VoiceSignalMessage } from './IVoiceSignaling.js';

export type VoicePeerEventHandler = (peer: VoicePeerConnection) => void;
export type VoiceRemoteStreamHandler = (peer: VoicePeerConnection, stream: MediaStream) => void;
export type VoiceControlMessageHandler = (peer: VoicePeerConnection, message: Record<string, unknown>) => void;

export class VoicePeerConnection {
  readonly remotePeerId: string;
  readonly polite: boolean;
  readonly config: VoiceChatConfig;
  readonly signaling: IVoiceSignaling;
  readonly pc: RTCPeerConnection;

  /** Reliable control channel — PTT, mute, presence. */
  controlChannel: RTCDataChannel | null = null;
  /** Unreliable real-time channel — VAD, position. */
  rtChannel: RTCDataChannel | null = null;

  private _makingOffer = false;
  private _ignoreOffer = false;
  private _isSettingRemoteAnswerPending = false;
  /** Idempotency guard so close()/onClosed fires exactly once per peer. */
  private _closed = false;

  onRemoteStream: VoiceRemoteStreamHandler | null = null;
  onControlMessage: VoiceControlMessageHandler | null = null;
  onClosed: VoicePeerEventHandler | null = null;

  constructor(
    remotePeerId: string, polite: boolean,
    config: VoiceChatConfig, signaling: IVoiceSignaling, localStream: MediaStream | null,
  ) {
    this.remotePeerId = remotePeerId;
    this.polite = polite;
    this.config = config;
    this.signaling = signaling;
    this.pc = new RTCPeerConnection({ iceServers: config.iceServers });

    // Wire local mic track if available (might be null in tests / lazy capture).
    if (localStream) {
      for (const track of localStream.getAudioTracks()) {
        this.pc.addTrack(track, localStream);
      }
    }

    // Use NEGOTIATED data channels with fixed IDs so both sides share the
    // same logical channel without negotiation. This avoids the previous
    // bug where both peers called `createDataChannel` non-negotiated and
    // ended up with FOUR channels per pair (two from each side). The
    // `ondatachannel` listener below is now a no-op for these channels
    // but remains for forward-compat with future caller-created channels.
    this.controlChannel = this.pc.createDataChannel('voice-control', {
      ordered: true, negotiated: true, id: 0,
    });
    this.rtChannel = this.pc.createDataChannel('voice-rt', {
      ordered: false, maxRetransmits: 0, negotiated: true, id: 1,
    });
    this._wireDataChannel(this.controlChannel);
    this._wireDataChannel(this.rtChannel);

    this.pc.addEventListener('negotiationneeded', async () => {
      try {
        this._makingOffer = true;
        await this.pc.setLocalDescription();
        const sdp = this.pc.localDescription?.sdp ?? null;
        signaling.send(remotePeerId, { kind: 'offer', payload: sdp });
      } catch { /* signaling error — caller handles via onClosed */ }
      finally {
        this._makingOffer = false;
      }
    });

    this.pc.addEventListener('icecandidate', (ev) => {
      if (ev.candidate) {
        signaling.send(remotePeerId, {
          kind: 'candidate',
          payload: JSON.stringify(ev.candidate.toJSON()),
        });
      }
    });

    this.pc.addEventListener('track', (ev) => {
      const stream = ev.streams[0];
      if (stream && this.onRemoteStream) this.onRemoteStream(this, stream);
    });

    this.pc.addEventListener('datachannel', (ev) => {
      this._wireDataChannel(ev.channel);
      if (ev.channel.label === 'voice-control') this.controlChannel = ev.channel;
      if (ev.channel.label === 'voice-rt')      this.rtChannel      = ev.channel;
    });

    this.pc.addEventListener('connectionstatechange', () => {
      if (
        (this.pc.connectionState === 'closed' || this.pc.connectionState === 'failed') &&
        !this._closed
      ) {
        this._closed = true;
        this.onClosed?.(this);
      }
    });
  }

  /**
   * Handle an inbound signaling message. Implements the perfect-negotiation
   * collision-resolution pattern.
   */
  async handleSignal(msg: VoiceSignalMessage): Promise<void> {
    try {
      if (msg.kind === 'offer' || msg.kind === 'answer') {
        const readyForOffer =
          !this._makingOffer &&
          (this.pc.signalingState === 'stable' || this._isSettingRemoteAnswerPending);
        const offerCollision = msg.kind === 'offer' && !readyForOffer;
        this._ignoreOffer = !this.polite && offerCollision;
        if (this._ignoreOffer) return;
        this._isSettingRemoteAnswerPending = msg.kind === 'answer';
        try {
          await this.pc.setRemoteDescription({
            type: msg.kind, sdp: msg.payload ?? '',
          });
        } finally {
          // A rejected SDP used to leave this true forever — readyForOffer then
          // always passed and the impolite peer stopped ignoring collisions.
          this._isSettingRemoteAnswerPending = false;
        }
        if (msg.kind === 'offer') {
          await this.pc.setLocalDescription();
          const sdp = this.pc.localDescription?.sdp ?? null;
          this.signaling.send(this.remotePeerId, { kind: 'answer', payload: sdp });
        }
      } else if (msg.kind === 'candidate' && msg.payload) {
        try {
          await this.pc.addIceCandidate(JSON.parse(msg.payload) as RTCIceCandidateInit);
        } catch {
          // Ignore ICE errors when we're deliberately rolling back (ignoreOffer).
          if (!this._ignoreOffer) throw new Error('addIceCandidate failed');
        }
      } else if (msg.kind === 'bye') {
        this.close();
      }
    } catch {
      // Silently on error — production should surface via EventBus.
      // Callers can watch onClosed / connectionState for diagnostics.
    }
  }

  /** Send a control (reliable) message. */
  sendControl(message: Record<string, unknown>): void {
    if (this.controlChannel?.readyState === 'open') {
      this.controlChannel.send(JSON.stringify(message));
    }
  }

  /** Send a real-time (unreliable) message — VAD, position updates. */
  sendRt(message: Record<string, unknown>): void {
    if (this.rtChannel?.readyState === 'open') {
      this.rtChannel.send(JSON.stringify(message));
    }
  }

  /**
   * Apply Opus quality preset via `RTCRtpSender.setParameters()` — the
   * modern non-SDP-munging path for bitrate. FEC/DTX knobs still require
   * SDP munging, which v1 doesn't implement (v2 roadmap).
   */
  async applyQuality(presetName: VoiceChatConfig['quality']): Promise<void> {
    const preset = VOICE_PRESETS[presetName];
    const sender = this.pc.getSenders().find((s) => s.track?.kind === 'audio');
    if (!sender) return;
    const params = sender.getParameters();
    if (!params.encodings) params.encodings = [{}];
    if (params.encodings.length === 0) params.encodings.push({});
    params.encodings[0].maxBitrate = preset.bitrate;
    try { await sender.setParameters(params); } catch { /* some browsers reject */ }
  }

  /** Close connection and fire onClosed (idempotent — safe to call twice). */
  close(): void {
    if (this._closed) return;
    this._closed = true;
    this.signaling.send(this.remotePeerId, { kind: 'bye', payload: null });
    try { this.pc.close(); } catch { /* ignore */ }
    // Per the W3C WebRTC spec, RTCPeerConnection.close() sets [[ConnectionState]]
    // to 'closed' synchronously WITHOUT queuing a 'connectionstatechange' task,
    // so the listener above never fires on a graceful close (and even if it did,
    // `_closed` is already true above, so its `!this._closed` guard would bail).
    // onClosed is therefore the ONLY path that removes the peer from the module's
    // map, releases spatial-router audio nodes, and fires onPeerLeft — fire it
    // here directly. The `_closed` guard keeps this exactly-once: if the browser
    // ever does emit a state change, the listener short-circuits.
    this.onClosed?.(this);
  }

  get connectionState(): RTCPeerConnectionState { return this.pc.connectionState; }

  private _wireDataChannel(channel: RTCDataChannel): void {
    channel.addEventListener('message', (ev) => {
      if (typeof ev.data === 'string' && this.onControlMessage) {
        try {
          const parsed = JSON.parse(ev.data) as Record<string, unknown>;
          this.onControlMessage(this, parsed);
        } catch { /* malformed message — drop */ }
      }
    });
  }
}
